import { Flex, Form, Select, Switch, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { Application } from 'src/api/main'
import { AppIcon } from 'src/features/apps/components/AppIcon'

const { Text } = Typography

const ICON_SIZE = 36
const INACTIVE_ICON_OPACITY = 0.55

export const activeKey = (appId: number) => `${appId}_active`
export const roleKey = (appId: number) => `${appId}_role`

// A row's role field holds an ARRAY of OrganizationRole ids for storage
// compatibility with the roles endpoint, but a member holds AT MOST ONE role
// per application — so the array carries zero or one id.
type FormShape = Record<string, boolean | number[] | undefined>

type ApplicationWithSettings = Application & {
  settings?: Record<string, unknown> | null
}

type Props = {
  app: ApplicationWithSettings
  form: ReturnType<typeof Form.useForm<FormShape>>[0]
  readOnly?: boolean
}

export const UserAppAccessRow = ({ app, form, readOnly = false }: Props) => {
  const { t } = useTranslation()
  const roles = app.related_roles ?? []
  const hasRole = roles.length > 0

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      form.setFieldValue(roleKey(app.id), [])
      return
    }
    // Activating an app with no role chosen would grant zero-permission access;
    // default to the first available role (admin can refine the multi-select).
    const current = (form.getFieldValue(roleKey(app.id)) ?? []) as number[]
    if (current.length === 0 && roles[0]?.id !== undefined) {
      form.setFieldValue(roleKey(app.id), [roles[0].id])
    }
  }

  return (
    <Form.Item
      shouldUpdate={(prev, next) =>
        prev[activeKey(app.id)] !== next[activeKey(app.id)] ||
        prev[roleKey(app.id)] !== next[roleKey(app.id)]
      }
      noStyle
    >
      {({ getFieldValue }) => {
        const isActive = Boolean(getFieldValue(activeKey(app.id)))
        const selectedRoleIds = (getFieldValue(roleKey(app.id)) ??
          []) as number[]
        const noRolePicked = selectedRoleIds.length === 0

        return (
          <Flex
            align="center"
            justify="space-between"
            gap={12}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--ant-color-bg-container)',
              border: '1px solid var(--ant-color-border-secondary)',
              transition: 'border-color 0.15s ease, background 0.15s ease'
            }}
          >
            <Flex align="center" gap={11} style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  display: 'inline-flex',
                  flex: '0 0 auto',
                  opacity: isActive ? 1 : INACTIVE_ICON_OPACITY,
                  transition: 'opacity 0.15s ease'
                }}
              >
                <AppIcon
                  type={app.instance_type.type}
                  photoUrl={app.icon?.file}
                  settings={app.settings}
                  size={ICON_SIZE}
                />
              </span>
              <Flex vertical style={{ minWidth: 0, lineHeight: 1.3 }}>
                <Flex align="center" gap={6}>
                  <Text
                    strong
                    style={{
                      fontSize: 13,
                      color: isActive
                        ? 'var(--ant-color-text)'
                        : 'var(--ant-color-text-secondary)'
                    }}
                    ellipsis
                  >
                    {app.name}
                  </Text>
                  {isActive && noRolePicked && hasRole ? (
                    <Tag
                      color="warning"
                      style={{ marginInlineEnd: 0, fontSize: 10 }}
                    >
                      {t('users.detail.access.pickRolePrompt')}
                    </Tag>
                  ) : null}
                </Flex>
                {app.description ? (
                  <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                    {app.description}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
            <Flex align="center" gap={10}>
              {/* Stored as an array; surfaced as one value because a member
                  holds a single role per app. */}
              <Form.Item
                name={roleKey(app.id)}
                noStyle
                getValueProps={(value: number[] | undefined) => ({
                  value: value?.[0]
                })}
                normalize={(value: number | undefined) =>
                  value == null ? [] : [value]
                }
              >
                <Select<number | undefined>
                  size="small"
                  style={{ minWidth: 200, maxWidth: 320 }}
                  placeholder={
                    hasRole
                      ? t('users.detail.access.rolePlaceholder')
                      : t('users.detail.access.noRoleAvailable')
                  }
                  allowClear
                  showSearch={{ optionFilterProp: 'label' }}
                  status={
                    isActive && hasRole && noRolePicked ? 'warning' : undefined
                  }
                  disabled={readOnly || !hasRole || !isActive}
                  options={roles.map((r) => ({
                    label: r.name,
                    value: r.id
                  }))}
                />
              </Form.Item>
              <Form.Item
                name={activeKey(app.id)}
                valuePropName="checked"
                noStyle
              >
                <Switch
                  size="small"
                  disabled={readOnly}
                  onChange={handleSwitchChange}
                />
              </Form.Item>
            </Flex>
          </Flex>
        )
      }}
    </Form.Item>
  )
}
